import React, { useCallback, useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import PathMarkerDiamondIcon from '../../PathBuilder/PathMarkerDiamondIcon';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { editor } from 'monaco-editor';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { getEnumOptions } from '../../../framework/framework-enums';
import type { ActionItem } from '../../../redux/EditorSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { frameworkActionArgsAreValid } from '../../../utils/sequenceTupleValidation';
import { updateRawActionArgValue, clearRawActionArgValue } from './driveModeAutonomousActionUtils';
import { excludeGroupOnlyActions } from './hardwareParser';
import { findActionDefForLabel } from './driveModeAutonomousActionUtils';
import { useAllAvailableActions } from '../../../hooks/editor/useAllAvailableActions';
import type { MarkerActionConfig, Path, PathMarker } from '../../PathBuilder/pathBuilderTypes';
import { formatMarkerPlacementLabel, getMarkerDisplayLetter } from '../../../utils/path-builder/path-marker-utils';
import ActionSelector from '../../Drawers/Controller/ActionSelector';
import FrameworkActionArgumentFields from '../../Drawers/Controller/FrameworkActionArgumentFields';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import type { FrameworkArgument } from '../../../framework/framework.d';
import { isTupleArg, tupleMemberValueKey } from '../../../utils/tupleArg';

interface MarkerRow {
  marker: PathMarker;
  path: Path;
}

interface PathMarkersControlListProps {
  /**
   * TODO(BPS-758): Wire Monaco/program-code generation once the framework exposes a
   * path-marker action API (e.g. follow_path_action marker callbacks). Until then,
   * assigned actions are persisted on path.mrk[].action in project JSON only.
   */
  editor?: editor.IStandaloneCodeEditor;
}

const enumOptions = getEnumOptions();

const PathMarkersControlList: React.FC<PathMarkersControlListProps> = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const iconFillColor = theme.vars!.palette.text.primary;
  const iconLetterColor = theme.vars!.palette.background.paper;
  const dispatch = useAppDispatch();
  const readOnly = useAppSelector((state) => state.project.loadedProject?.readOnly) ?? false;
  const paths = useAppSelector((state) => state.project.loadedProject?.paths ?? []) as Path[];
  const editorActionList = useAppSelector((state) => state.editor.actionList) as ActionItem[];
  const programCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const allAvailableActions = useAllAvailableActions();
  const pathOptions = useMemo<Array<{ id: string; name: string }>>(() => paths.map((p) => ({ id: p.id, name: p.name })), [paths]);
  // TODO(BPS-758): Once framework defines path-marker actions, derive selectable actions from
  // the new API and sync assignments to program code (Monaco on programming page; string-level
  // helpers on Path Builder). For now we use editor.actionList, which may be empty on Path
  // Builder when Monaco has not populated editor state.
  const selectableActions = useMemo(() => excludeGroupOnlyActions(editorActionList), [editorActionList]);

  const [dialogPathId, setDialogPathId] = useState<string | null>(null);
  const [dialogMarkerId, setDialogMarkerId] = useState<string | null>(null);
  const [draftSelection, setDraftSelection] = useState<string>('');
  const [draftArgValues, setDraftArgValues] = useState<Record<string, string>>({});

  const allMarkerRows = useMemo<MarkerRow[]>(() => paths.flatMap((path) => (path.mrk ?? []).map((marker) => ({ marker, path }))), [paths]);

  const dialogMarker = useMemo<PathMarker | null>(() => {
    if (!dialogMarkerId || !dialogPathId) return null;
    const path = paths.find((p) => p.id === dialogPathId);
    return path?.mrk?.find((m) => m.id === dialogMarkerId) ?? null;
  }, [dialogMarkerId, dialogPathId, paths]);

  const dialogActionDef = useMemo(
    () => withResolvedMotorVelocityLimits(findActionDefForLabel(allAvailableActions, draftSelection), draftArgValues),
    [allAvailableActions, draftArgValues, draftSelection]
  );

  const dialogCanSave = useMemo(
    () => !draftSelection || frameworkActionArgsAreValid(dialogActionDef, draftArgValues, t, { programCode }),
    [dialogActionDef, draftArgValues, draftSelection, programCode, t]
  );

  const openDialog = useCallback(
    (pathId: string, markerId: string) => {
      if (readOnly) return;
      const path = paths.find((p) => p.id === pathId);
      const marker = path?.mrk?.find((m) => m.id === markerId);
      setDialogPathId(pathId);
      setDialogMarkerId(markerId);
      setDraftSelection(marker?.action?.selection ?? '');
      setDraftArgValues(marker?.action?.argValues ?? {});
    },
    [paths, readOnly]
  );

  const closeDialog = useCallback(() => {
    setDialogPathId(null);
    setDialogMarkerId(null);
    setDraftSelection('');
    setDraftArgValues({});
  }, []);

  const handleActionSelect = useCallback((selection: string) => {
    setDraftSelection(selection);
    // Reset arg values when the action changes so stale keys don't carry over.
    setDraftArgValues({});
  }, []);

  const handleArgChange = useCallback(
    (name: string, value: string) => {
      setDraftArgValues((prev) => {
        const next = updateRawActionArgValue(prev, name, value);
        const actionDef = findActionDefForLabel(allAvailableActions, draftSelection);
        const tupleArg = (actionDef?.arguments ?? []).find((arg: FrameworkArgument) =>
          isTupleArg(arg) ? arg.members?.some((member) => tupleMemberValueKey(arg.name, member.name) === name) : false
        ) as FrameworkArgument | undefined;
        if (tupleArg) {
          delete next[tupleArg.name];
          clearRawActionArgValue(next, tupleArg.name);
        }
        return next;
      });
    },
    [allAvailableActions, draftSelection]
  );

  const handleArgValuesChange = useCallback((next: Record<string, string>) => {
    setDraftArgValues(next);
  }, []);

  const handleSave = useCallback(() => {
    if (!dialogMarkerId || !dialogPathId || readOnly) return;
    const path = paths.find((p) => p.id === dialogPathId);
    const marker = path?.mrk?.find((m) => m.id === dialogMarkerId);
    if (!marker) return;

    // TODO(BPS-758): After assigning/updating marker.action, emit the corresponding Python
    // snippet into project.program.code (dashboard-style string helpers on Path Builder;
    // Monaco edits on programming page). Blocked on framework API definition.
    const action: MarkerActionConfig | undefined = draftSelection.trim()
      ? { selection: draftSelection.trim(), argValues: draftArgValues }
      : undefined;

    dispatch(
      projectSlice.actions.updatePathMarker({
        pathId: dialogPathId,
        marker: { ...marker, action },
      })
    );
    closeDialog();
  }, [dialogMarkerId, dialogPathId, paths, draftSelection, draftArgValues, readOnly, dispatch, closeDialog]);

  const handleClearAction = useCallback(
    (pathId: string, marker: PathMarker) => {
      if (readOnly) return;
      dispatch(
        projectSlice.actions.updatePathMarker({
          pathId,
          marker: { ...marker, action: undefined },
        })
      );
    },
    [readOnly, dispatch]
  );

  const assignedRows = useMemo(() => allMarkerRows.filter(({ marker }) => marker.action?.selection), [allMarkerRows]);

  if (allMarkerRows.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Trans i18nKey="pathMarkers.control.empty">No markers have been added. Add markers from the Path Builder.</Trans>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Marker list */}
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            <Trans i18nKey="pathMarkers.control.markersTitle">Markers</Trans>
          </Typography>
          {allMarkerRows.map(({ marker, path }) => {
            const actionLabel = marker.action?.selection ?? '';
            const editLabel = t('pathMarkers.control.editMarkerAria', {
              name: marker.name,
              defaultValue: 'assign action to {{name}}',
            });
            const clearLabel = t('pathMarkers.control.clearMarkerAria', {
              name: marker.name,
              defaultValue: 'clear action for {{name}}',
            });
            return (
              <Stack
                key={marker.id}
                direction="row"
                alignItems="center"
                spacing={0.5}
                data-testid={`marker-row-${marker.id}`}
                onClick={() => !readOnly && openDialog(path.id, marker.id)}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <PathMarkerDiamondIcon
                  letter={getMarkerDisplayLetter(marker.name)}
                  size={32}
                  fillColor={iconFillColor}
                  letterColor={iconLetterColor}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {path.name} / {marker.name}
                  </Typography>
                  {actionLabel && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {actionLabel}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={editLabel}>
                  <span>
                    <IconButton
                      aria-label={editLabel}
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDialog(path.id, marker.id);
                      }}
                      disabled={readOnly}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {actionLabel && (
                  <Tooltip title={clearLabel}>
                    <span>
                      <IconButton
                        aria-label={clearLabel}
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClearAction(path.id, marker);
                        }}
                        disabled={readOnly}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>

        {/* Action summary */}
        {assignedRows.length > 0 && (
          <>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                <Trans i18nKey="pathMarkers.control.actionsTitle">Actions</Trans>
              </Typography>
              {assignedRows.map(({ marker, path }) => {
                const placementLabel = formatMarkerPlacementLabel(marker, t);
                const summaryText = t('pathMarkers.control.actionSummaryRow', {
                  name: marker.name,
                  type: placementLabel,
                  value: marker.value,
                  action: marker.action!.selection,
                  defaultValue: '{{name}} — {{type}} ({{value}}) — {{action}}',
                });
                return (
                  <Stack key={`${path.id}-${marker.id}`} direction="row" alignItems="center" spacing={1}>
                    <PathMarkerDiamondIcon size={20} fillColor={iconFillColor} letterColor={iconLetterColor} />
                    <Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
                      {summaryText}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                      {path.name}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>

      {/* Action assignment dialog */}
      <Dialog open={dialogMarker !== null} onClose={closeDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('pathMarkers.control.dialogTitle', {
            name: dialogMarker?.name ?? '',
            defaultValue: 'Assign Action — {{name}}',
          })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ActionSelector
              value={draftSelection}
              onChange={handleActionSelect}
              actions={selectableActions}
              allowNone
              noneLabel={t('pathMarkers.control.noAction', 'None')}
              ariaLabel={t('pathMarkers.control.actionSelectorAria', 'select action for marker')}
            />
            {draftSelection && (
              <FrameworkActionArgumentFields
                actionDef={dialogActionDef}
                values={draftArgValues}
                onArgChange={handleArgChange}
                onArgValuesChange={handleArgValuesChange}
                pathOptions={pathOptions}
                enumOptions={enumOptions}
                t={t}
                layout="stack"
                showUnits={false}
                spacing={1}
                readOnly={readOnly}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common.cancel', { defaultValue: 'CANCEL' })}</Button>
          <Button onClick={handleSave} variant="contained" disabled={readOnly || !dialogCanSave}>
            {t('common.update', { defaultValue: 'UPDATE' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PathMarkersControlList;
